/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	
	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		colorSelector: 'color-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

};

// Klaviyo "Back in stock" custom popup - API revision (client back-in-stock-subscriptions endpoint)
const KLAVIYO_BIS_API_REVISION = '2024-10-15';

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};
	
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

/**
 * Fetch a section render as a parsed HTML document, with per-call caching.
 */
async function fetchSectionHTML(url, cache) {
	if (cache.has(url)) {
		return cache.get(url);
	}

	const response = await fetch(url);
	if (!response.ok) throw new Error(`HTTP ${response.status}`);

	const responseText = await response.text();
	const html = new DOMParser().parseFromString(responseText, 'text/html');

	cache.set(url, html);
	return html;
}

/**
 * Re-fetch the current variant's markup and refresh [data-product-reload] elements
 * (quantity selector, CTA...) so stock-dependent state reflects what was just added to cart.
 * Bypasses fetchSectionHTML's cache since the cart — not the variant — changed.
 */
async function refreshProductStockState() {
	const variantSelector = document.querySelector(PRODUCT_CONFIG.selectors.variantSelector);
	const baseUrl = variantSelector?.dataset.url || window.location.pathname;
	const sectionId = variantSelector?.dataset.section;
	const variantId = document.querySelector(PRODUCT_CONFIG.selectors.variantInput)?.value;

	if (!sectionId) return;

	const url = `${baseUrl}?variant=${variantId || ''}&section_id=${sectionId}`;

	try {
		const html = await fetchSectionHTML(url, new Map());
		ProductManager.updateReloadElements(html);
	} catch (error) {
		window.log('warn', 'Product', 'Failed to refresh stock state after add-to-cart', { error: error.message });
	}
}

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;
		
		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			// Programmatic .value assignment fires no native event — dispatch one so Shopify's
			// accelerated checkout button (which listens on this input) picks up the new variant.
			variantInput.dispatchEvent(new Event('change', { bubbles: true }));
			window.log('info', 'Product', 'Variant ID updated', {
				oldVariantId: oldValue, 
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		
		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;
		
		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;
			
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls => 
					cls.includes('component-price') || 
					cls.includes('component-quantity') || 
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);
				
				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}
			
			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}
			
			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					productEmit('product-reload-element-updated', { 
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					window.log('warn', 'Product', 'Element update failed', { 
						index, 
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message 
					});
				}
			}
		});

		window.log('info', 'Product', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});
		
		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;
		
		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm);
		if (!this.container) {
			window.log('error', 'Product', 'VariantSelector: No product form container found');
			return;
		}
		
		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		productEmit('variant-selector-init-start');
		
		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();
			
			productEmit('variant-selector-init-success');
		} catch (error) {
			window.log('error', 'Product', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange() {
		productEmit('variant-change-start');

		try {
			setTimeout(() => {
				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					productEmit('variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					this.updateURL();
					this.updateProductData();
					this.updateMediaDisplay();
				} else {
					window.log('warn', 'Product', 'No matching variant found for options', { options: this.options });
					productEmit('variant-not-found', { options: this.options });
				}
			}, 10);
		} catch (error) {
			window.log('error', 'Product', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);
		
		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);

			// outerHTML replacement in updateReloadElements destroys [data-cta]/[data-sticky-cta],
			// detaching the IntersectionObserver — reattach it on the new nodes.
			initializeStickyCTA();
		}
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			window.log('warn', 'Product', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;
		
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		
		if (!sectionId) {
			window.log('warn', 'Product', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		try {
			const html = await fetchSectionHTML(variantUrl, this.cached);
			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			window.log('error', 'Product', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs
	 */
	getSelectedOptions() {
		const checkboxes = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantCheckbox);
		this.options = Array.from(checkboxes, checkbox => checkbox.value).filter(value => value);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();
		
		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');
			
			if (!jsonElement) {
				window.log('error', 'Product', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}
			
			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				window.log('error', 'Product', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 */
	updateURL() {
		if (!this.currentVariant?.id) return;
		
		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', 'Product', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		
		if (!newOptions.length) return;

		currentOptions.forEach((currentOption, index) => {
			const newOption = newOptions[index];
			if (!newOption) return;

			const currentInput = currentOption.querySelector('input');
			const newInput = newOption.querySelector('input');
			
			if (currentInput && newInput) {
				// Update only disabled state, preserve checked state
				currentInput.disabled = newInput.disabled;
				
				const currentlyChecked = currentInput.checked;
				currentOption.className = newOption.className;
				
				if (currentlyChecked !== currentInput.checked) {
					currentInput.checked = currentlyChecked;
				}
			}
		});
	}
}

/*********************************************************************************************
 * 🎨 COLOR SELECTION LOGIC (product-to-product swap, no page reload)
 */
class ColorSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.isLoading = false;

		this.onClick = this.onClick.bind(this);
	}

	connectedCallback() {
		this.addEventListener('click', this.onClick);
	}

	get gridElement() {
		return document.getElementById(this.dataset.gridId);
	}

	onClick(event) {
		const link = event.target.closest('a.mm-product-color-link');
		if (!link || !this.contains(link)) return;

		// Let the browser handle new-tab / new-window intents natively
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}

		if (this.isLoading) {
			event.preventDefault();
			return;
		}

		event.preventDefault();
		this.swapTo(link.href, { pushHistory: true });
	}

	async swapTo(url, { pushHistory }) {
		const sectionId = this.dataset.section;
		if (!sectionId) {
			window.log('warn', 'Product', 'ColorSelector: No section ID provided');
			window.location.href = url;
			return;
		}

		this.isLoading = true;
		this.classList.add('mm-is-loading');
		productEmit('color-swap-start', { url });

		try {
			const fetchUrl = new URL(url, window.location.origin);
			fetchUrl.searchParams.set('section_id', sectionId);

			const html = await fetchSectionHTML(fetchUrl.toString(), this.cached);
			this.applySwap(html, url);

			if (pushHistory) {
				window.history.pushState({ mm: 'color-swap' }, '', url);
			}

			productEmit('color-swap-complete', { url });
		} catch (error) {
			window.log('error', 'Product', 'ColorSelector: Failed to swap color, falling back to native navigation', { error: error.message });
			window.location.href = url;
		} finally {
			this.isLoading = false;
			this.classList.remove('mm-is-loading');
		}
	}

	applySwap(html, url) {
		const grid = this.gridElement;
		if (!grid) {
			window.log('warn', 'Product', 'ColorSelector: Grid element not found', { gridId: this.dataset.gridId });
			window.location.href = url;
			return;
		}

		const newGrid = html.getElementById(this.dataset.gridId);
		if (!newGrid) {
			window.log('warn', 'Product', 'ColorSelector: New grid markup not found in response');
			window.location.href = url;
			return;
		}

		window.destroySliderPair?.('main');
		window.destroySliderPair?.('variant');

		grid.outerHTML = newGrid.outerHTML;

		// outerHTML replacement destroys the old form nodes, taking their attached
		// listeners with them — reattach the add-to-cart handler on the new form.
		window.initAddToCartForms?.();

		window.initializeSliderPair?.('main', false);
		window.initializeSliderPair?.('variant', true);
		initializeStickyCTA();
	}
}

// Handle browser back/forward across color swaps
window.addEventListener('popstate', (event) => {
	if (event.state?.mm !== 'color-swap') return;

	const selector = document.querySelector(PRODUCT_CONFIG.selectors.colorSelector);
	selector?.swapTo(window.location.href, { pushHistory: false });
});

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				const totalPrice = (priceValue * quantity) / 100;
				const currency = window.Shopify?.currency?.active || 'EUR';
				const locale = document.documentElement.lang || 'en';
				const formattedPrice = new Intl.NumberFormat(locale, {
					style: 'currency',
					currency: currency
				}).format(totalPrice);
				priceElement.textContent = formattedPrice;
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', { 
			quantity, 
			updatedElements: updatedElementsCount 
		});
		
		window.log('info', 'Product', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to update prices', { 
			quantity, 
			error: error.message 
		});
		productEmit('quantity-update-error', { 
			quantity, 
			error: error.message 
		});
	}
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 */
function initializeStickyCTA() {
	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;

	if (!classicCTA || !stickyCTA) {
		window.log('warn', 'Product', 'StickyCTA: Required elements not found');
		return;
	}

	try {
		const mobileQuery = window.matchMedia('(max-width: 768px)');
		let classicCTAVisible = false;
		let footerVisible = false;

		const updateVisibility = () => {
			stickyCTA.classList.toggle('mm-none', classicCTAVisible || footerVisible || !mobileQuery.matches);
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				classicCTAVisible = entry.isIntersecting;
				updateVisibility();
				// Show infos only when user has scrolled past the main CTA (CTA above viewport)
				const scrolledPast = entry.boundingClientRect.top < 0;
				stickyCTA.classList.toggle('mm-product-sticky-cta--past-cta', scrolledPast);
			});
		}, {
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});

		observer.observe(classicCTA);
		classicCTA._stickyObserver = observer;

		const footer = document.querySelector('footer');
		if (footer) {
			const footerObserver = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					footerVisible = entry.isIntersecting;
					updateVisibility();
				});
			}, { threshold: 0 });
			footerObserver.observe(footer);
		}

	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/*********************************************************************************************
 * 🔔 KLAVIYO "BACK IN STOCK" CUSTOM POPUP
 */

function buildKlaviyoClientBisSubscribeUrl(publicKey) {
	return `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${publicKey}`;
}

function klaviyoShopifyCatalogVariantId(variantId) {
	return `$shopify:::$default:::${variantId}`;
}

function initKlaviyoBackInStock() {
	document.addEventListener('submit', async (e) => {
		const form = e.target.closest('[data-klaviyo-bis-form-element]');
		if (!form) return;
		e.preventDefault();

		const root = form.closest('[data-klaviyo-bis-root]');
		const publicKey = root?.dataset.klaviyoPublicKey;
		const emailInput = form.querySelector('[data-klaviyo-bis-email]');
		const errorEmail = form.querySelector('[data-klaviyo-bis-error-email]');
		const errorGeneric = form.querySelector('[data-klaviyo-bis-error-generic]');
		const successEl = root?.querySelector('[data-klaviyo-bis-success]');
		const formWrapper = root?.querySelector('[data-klaviyo-bis-form]');
		const submitBtn = form.querySelector('[data-klaviyo-bis-submit]');
		const variantInput = document.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		const email = emailInput?.value.trim();

		errorEmail?.setAttribute('hidden', '');
		errorGeneric?.setAttribute('hidden', '');

		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!email || !emailPattern.test(email)) {
			errorEmail?.removeAttribute('hidden');
			return;
		}

		if (!publicKey || !variantInput?.value) {
			window.log('warn', 'Product', 'Klaviyo BIS: missing public key or variant id');
			errorGeneric?.removeAttribute('hidden');
			return;
		}

		if (submitBtn) submitBtn.disabled = true;

		try {
			const response = await fetch(buildKlaviyoClientBisSubscribeUrl(publicKey), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/vnd.api+json',
					Accept: 'application/vnd.api+json',
					revision: KLAVIYO_BIS_API_REVISION
				},
				body: JSON.stringify({
					data: {
						type: 'back-in-stock-subscription',
						attributes: {
							profile: {
								data: {
									type: 'profile',
									attributes: { email }
								}
							},
							channels: ['EMAIL']
						},
						relationships: {
							variant: {
								data: {
									type: 'catalog-variant',
									id: klaviyoShopifyCatalogVariantId(variantInput.value)
								}
							}
						}
					}
				})
			});

			if (!response.ok && response.status !== 202) throw new Error(`Klaviyo BIS request failed (${response.status})`);

			if (formWrapper) formWrapper.setAttribute('hidden', '');
			successEl?.removeAttribute('hidden');
			productEmit('klaviyo-bis-subscribed', { variantId: variantInput.value });
		} catch (error) {
			window.log('error', 'Product', 'Klaviyo BIS subscribe failed', { error: error.message });
			errorGeneric?.removeAttribute('hidden');
		} finally {
			if (submitBtn) submitBtn.disabled = false;
		}
	});
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom elements
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);
customElements.define(PRODUCT_CONFIG.selectors.colorSelector, ColorSelector);

/*********************************************************************************************
 * 🚀 INITIALIZATION & ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		initializeStickyCTA();
		productEmit('initialized');
		window.log('info', 'Product', 'Product page initialized successfully');
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize product page', { error: error.message });
	}

	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}

	});

	// Clamp manually typed values (input has no native min/max enforcement since type="text")
	document.addEventListener('change', (e) => {
		const input = e.target.closest('[data-qtty-input]');
		if (!input) return;

		const minValue = parseInt(input.getAttribute('min'), 10) || 1;
		const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
		const currentValue = parseInt(input.value, 10) || minValue;
		const clampedValue = Math.max(minValue, Math.min(maxValue, currentValue));

		if (clampedValue !== currentValue) {
			input.value = clampedValue;
		}

		mmUpdatePrice(clampedValue);
		updateQuantityButtonStates(input);
	});

	initKlaviyoBackInStock();

	// Refresh quantity selector / CTA disabled state once the cart actually reflects the new item
	document.addEventListener('cart:add-to-cart-success', () => {
		refreshProductStockState();
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			updateQuantityButtonStates(input);
		});

		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}